import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import ImageThumb from "@/components/ImageThumb";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, Clock, Banknote, ShoppingBag, Tag,
  ShieldCheck, PackageCheck, Loader2, Sparkles,
} from "lucide-react";

const STATUS = {
  held: { label: "Pending — in guarantee", color: "#EA580C" },
  released: { label: "Cleared", color: "#16A34A" },
  refunded: { label: "Refunded to buyer", color: "#E11D48" },
  paid_out: { label: "Withdrawn", color: "#697169" },
};

function Card({ label, value, sub, color, testid }) {
  return (
    <div className="hairline p-6 bg-white" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-3xl tracking-tighter mt-2" style={color ? { color } : {}}>
        ${Number(value || 0).toFixed(2)}
      </div>
      {sub && <p className="mt-1 text-xs text-neutral-500">{sub}</p>}
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[0.7rem] font-medium"
        style={{ background: "#1E3A2B", color: "#F9F6F0" }}>{n}</span>
      <span className="text-sm text-neutral-700 leading-relaxed">{children}</span>
    </li>
  );
}

function Guide({ icon: Icon, title, steps, testid }) {
  return (
    <div className="hairline p-6 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color: "#1E3A2B" }} />
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <ol className="mt-4 space-y-3">
        {steps.map((s, i) => <Step key={s} n={i + 1}>{s}</Step>)}
      </ol>
    </div>
  );
}

export default function Wallet() {
  const [w, setW] = useState(null);
  const [spot, setSpot] = useState(null);
  const [activating, setActivating] = useState(false);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [stripeLive, setStripeLive] = useState(true);

  const load = () => api.get("/wallet").then((r) => setW(r.data)).catch(() => {});
  const loadSpot = () => api.get("/seller/spotlight").then((r) => setSpot(r.data)).catch(() => {});
  const loadConnect = () => api.get("/seller/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect({ connected: false, payouts_enabled: false }));
  useEffect(() => {
    load(); loadSpot(); loadConnect();
    api.get("/config").then((r) => setStripeLive(!!r.data.stripe_live)).catch(() => {});
    const q = new URLSearchParams(window.location.search).get("connect");
    if (q === "done") {
      toast.success("Payout details saved — verifying with Stripe. This can take a moment.");
      window.history.replaceState({}, "", "/wallet");
    } else if (q === "refresh") {
      toast("Setup link expired — tap 'Set up payouts' to continue.");
      window.history.replaceState({}, "", "/wallet");
    }
  }, []);

  const onboard = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/seller/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setConnecting(false); }
  };

  const activateSpotlight = async () => {
    setActivating(true);
    try {
      const { data } = await api.post("/seller/spotlight", {});
      toast.success(`Closet Spotlight on — your whole store is bumped for ${data.days} days ($${data.price.toFixed(2)}/mo, demo)`);
      loadSpot();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setActivating(false); }
  };

  const tx = w?.transactions || [];

  return (
    <div data-testid="wallet-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-6">
        <div className="label-eyebrow">Your money</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Wallet.</h1>
        <p className="mt-3 text-sm text-neutral-500 max-w-2xl">
          Everything you earn from selling lands here. Funds clear after the {w?.guarantee_days || 3}-day buyer
          guarantee, then they're paid to your debit card automatically.
        </p>
      </div>

      {/* Balances */}
      <section className="mt-8 grid sm:grid-cols-3 gap-4">
        <Card testid="wallet-available" label="Available" value={w?.available} color="#16A34A"
          sub={connect?.payouts_enabled ? "Cleared — heading to your card" : "Cleared funds"} />
        <Card testid="wallet-pending" label="Pending" value={w?.pending} color="#EA580C"
          sub={w?.next_release
            ? `Next payout ~ ${new Date(w.next_release).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
            : `Held during the ${w?.guarantee_days || 3}-day guarantee`} />
        <Card testid="wallet-lifetime" label="Lifetime earnings" value={w?.lifetime_net}
          sub="Net of fees, across all sales" />
      </section>

      {/* Payouts to card (Stripe Connect) */}
      <div className="mt-4 hairline p-6 bg-white" data-testid="payouts-panel"
        style={{ borderColor: connect?.payouts_enabled ? "#16A34A" : "#1E3A2B" }}>
        <div className="flex items-center gap-2">
          <Banknote size={15} style={{ color: connect?.payouts_enabled ? "#16A34A" : "#1E3A2B" }} />
          <span className="label-eyebrow">Payouts to your card</span>
          {connect?.payouts_enabled && (
            <span className="chip" data-active="true" data-testid="payouts-active">Active</span>
          )}
        </div>
        {!connect ? (
          <p className="mt-2 text-sm text-neutral-500">Loading…</p>
        ) : connect.payouts_enabled ? (
          <p className="mt-2 text-sm text-neutral-600 max-w-xl" data-testid="payouts-status-active">
            You're all set. Cleared earnings are sent to your debit card automatically after the{" "}
            {w?.guarantee_days || 3}-day buyer guarantee — securely through Stripe. No bank routing numbers needed.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-neutral-600 max-w-xl">
              {connect.connected
                ? "Almost there — finish the quick Stripe setup to start getting paid to your debit card."
                : "Link a debit card once, securely through Stripe, and your sales are paid straight to your card after the buyer-protection window. We never see your card or bank details."}
            </p>
            <button className="btn-primary mt-4" onClick={onboard} disabled={connecting} data-testid="connect-onboard-btn">
              {connecting ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
              {connect.connected ? "Finish payout setup" : "Set up payouts to my card"}
            </button>
          </>
        )}
        <p className="mt-3 text-[0.62rem] text-neutral-400">
          {stripeLive ? "Powered by Stripe Connect — secure payouts." : "Test mode — powered by Stripe Connect. No real money moves yet."}
        </p>
      </div>

      {spot && (
        <div className="mt-4 hairline p-6 bg-white flex flex-wrap items-center justify-between gap-4"
          style={{ borderColor: "#2563EB" }} data-testid="spotlight-panel">
          <div className="max-w-xl">
            <div className="flex items-center gap-2">
              <Sparkles size={15} style={{ color: "#2563EB" }} />
              <span className="label-eyebrow" style={{ color: "#2563EB" }}>Closet Spotlight</span>
              {spot.active && <span className="chip" data-active="true" data-testid="spotlight-active">Active</span>}
            </div>
            <p className="text-sm text-neutral-600 mt-2">
              {spot.active
                ? `Your whole closet is bumped to the top of Browse until ${new Date(spot.spotlight_until).toLocaleDateString()}.`
                : `Keep every item in your closet bumped to the top for ${spot.days} days at a time.`}
              <span className="text-neutral-400"> Billed ${spot.price.toFixed(2)}/mo — demo, no real charge.</span>
            </p>
          </div>
          <button className="btn-klein" onClick={activateSpotlight} disabled={activating} data-testid="spotlight-btn">
            {activating ? "Activating…" : spot.active ? `Renew ($${spot.price.toFixed(2)}/mo)` : `Activate ($${spot.price.toFixed(2)}/mo)`}
          </button>
        </div>
      )}

      {/* Ledger */}
      <section className="mt-10">
        <div className="hairline-b pb-3 flex items-baseline justify-between">
          <h2 className="font-display text-2xl">Transactions</h2>
          <span className="label-eyebrow">{tx.length} entr{tx.length === 1 ? "y" : "ies"}</span>
        </div>
        {tx.length === 0 ? (
          <div className="hairline p-14 text-center mt-6" data-testid="wallet-empty">
            <div className="label-eyebrow">No activity yet</div>
            <p className="mt-2 text-sm text-neutral-500">Once you make a sale, its earnings show up here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-black/10" data-testid="wallet-ledger">
            {tx.map((t, i) => {
              const st = STATUS[t.status] || STATUS.held;
              const isWithdrawal = t.type === "withdrawal" || t.type === "payout";
              return (
                <li key={t.id || `${t.title}-${t.date}-${i}`} className="py-4 flex items-center gap-4" data-testid={`wallet-tx-${i}`}>
                  <div className="w-11 h-11 hairline overflow-hidden shrink-0 flex items-center justify-center bg-neutral-50">
                    {isWithdrawal
                      ? <Banknote size={16} className="text-neutral-500" />
                      : <ImageThumb path={t.image_path} className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm truncate">{t.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: st.color }}>{st.label}</div>
                    {t.status === "held" && (
                      <div className="text-[0.62rem] text-neutral-400 mt-0.5" data-testid={`wallet-release-${i}`}>
                        {t.release_date
                          ? `${t.release_estimated ? "Clears ~" : "Clears"} ${new Date(t.release_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                          : "Clears after it ships + guarantee"}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display" style={t.net < 0 ? { color: "#697169" } : {}}>
                      {t.net < 0 ? "-" : "+"}${Math.abs(t.net).toFixed(2)}
                    </div>
                    {!isWithdrawal && t.gross != null && (
                      <div className="text-[0.62rem] uppercase tracking-[0.12em] text-neutral-400 mt-0.5">
                        ${t.gross.toFixed(2)} − ${t.fee.toFixed(2)} fee
                      </div>
                    )}
                    {t.date && <div className="text-[0.62rem] text-neutral-400 mt-0.5">{new Date(t.date).toLocaleDateString()}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* How the wallet works */}
      <section className="mt-14">
        <div className="hairline-b pb-3">
          <div className="label-eyebrow">Good to know</div>
          <h2 className="font-display text-2xl mt-1">How your wallet works</h2>
        </div>
        <ol className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wallet-how">
          {[
            "A sale's earnings (after fees) enter your wallet as Pending.",
            `They stay Pending while the ${w?.guarantee_days || 3}-day buyer guarantee runs.`,
            "When the window closes with no claim, funds become Available.",
            "Cleared funds are paid to your linked debit card automatically — no manual step.",
          ].map((s, i) => (
            <li key={s} className="hairline p-5 bg-white">
              <div className="font-display text-3xl tracking-tighter text-neutral-300">{i + 1}</div>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{s}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* SOP: How the site works */}
      <section className="mt-14" data-testid="sop-section">
        <div className="hairline-b pb-3">
          <div className="label-eyebrow">The short version</div>
          <h2 className="font-display text-2xl mt-1">How Your Classic Style works</h2>
          <p className="mt-2 text-sm text-neutral-500 max-w-2xl">A quick playbook — the essentials, start to finish.</p>
        </div>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <Guide icon={Tag} title="Selling" testid="sop-selling" steps={[
            "Create a listing: add at least 3 photos plus a label close-up and any flaw shots.",
            "Grade it honestly (Good or better), add exact measurements and disclosures.",
            "Confirm it's legal and safe to ship, then publish. Branded items pass a quick AI authenticity check.",
            "When it sells, inspect the item and mark it shipped with tracking.",
            "Earnings clear after the 3-day guarantee and are paid to your card.",
          ]} />
          <Guide icon={ShoppingBag} title="Buying" testid="sop-buying" steps={[
            "Browse or search, and save pieces to get alerts on matches.",
            "Add to bag, or accept a seller's private offer.",
            "Choose delivery — locker, home or local pickup — and pay securely with Stripe.",
            "Track your parcel from the Purchases page.",
            "On delivery, a 3-day authenticity guarantee begins.",
          ]} />
          <Guide icon={ShieldCheck} title="Buyer protection" testid="sop-protection" steps={[
            "Every order is covered for 3 days after delivery.",
            "The seller's payout is held during that window.",
            "If an item is fake or not as described, open a claim with photos from Purchases.",
            "Our AI reviews the evidence; approved claims are refunded and inconclusive ones side with the buyer.",
          ]} />
        </div>
        <p className="mt-6 text-xs text-neutral-500">
          Want the fine print? Read the full{" "}
          <a href="/terms" className="underline" data-testid="sop-terms-link">Terms &amp; Conditions</a>{" "}
          and the{" "}
          <a href="/proof-guide" className="underline">authenticity proof guide</a>.
        </p>
      </section>
    </div>
  );
}
